import { gql, useQuery } from '@apollo/client';
import {
  TRANSLATED_STRING_FIELDS,
  translateBackend,
} from '@assets/i18n/config';
import type { TranslatedString } from '@interfaces/shared';
import { Skeleton } from '@ovComponents/1-primative/skeleton/skeleton';
import { Typography } from '@ovComponents/1-primative/typography/typography';
import { Button } from '@ovComponents/2-component/button/button';
import { Card, CardContent } from '@ovComponents/2-component/card/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHeadCell,
  TableRow,
} from '@ovComponents/2-component/table/table';
import { UserContext, usePermissions } from '@providers/userContextProvider';
import { useLocalization } from '@utils/useLocalization';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

const FETCH_WORKFLOWS = gql`
  query fetchWorkflowCompletions($input: FetchWorkflowCompletionsInput!) {
    fetchWorkflowCompletions(input: $input) {
      workflowCompletions {
        id
        objectId
        objectType
        context
        object {
          ... on User {
            firstName
            lastName
            entityName
            type
          }
        }
        workflow {
          name { ${TRANSLATED_STRING_FIELDS} }
        }
        createdAt
        currentStep {
          name { ${TRANSLATED_STRING_FIELDS} }
        }
        currentSubStep {
          type
          options
        }
        state
      }
      totalCount
    }
  }
`;

export const FETCH_ACTIVE_WORKFLOWS = gql`
  query fetchWorkflows($input: FetchWorkflowsInput!) {
    fetchWorkflows(input: $input) {
      totalCount
      workflows {
        id
        name { ${TRANSLATED_STRING_FIELDS} }
        steps {
          id
          name { ${TRANSLATED_STRING_FIELDS} }
          subSteps {
            id
            type
            options
          }
        }
      }
    }
  }
`;

export const OpenWorkflows = ({
  options,
}: {
  options: {
    customTitle: TranslatedString;
  };
}) => {
  const { permissions } = usePermissions();
  const { t } = useTranslation(['workflowCompletions']);
  const { activeOrganization } = useContext(UserContext);
  const { localizedDateTime } = useLocalization();
  const navigate = useNavigate();
  const pageSize = 5;
  const { loading, data, previousData } = useQuery(FETCH_WORKFLOWS, {
    variables: {
      input: {
        filter: {
          organizationId: activeOrganization.id,
          state: 'ACTIVE',
        },
        pagination: {
          sortField: 'createdAt',
          sortDesc: true,
          perPage: pageSize,
          offSet: 0,
        },
      },
    },
    skip: !permissions.includes('read:workflow_completion'),
    fetchPolicy: 'no-cache',
  });

  return (
    <Card loading={loading}>
      <CardContent
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography variant='titleLarge'>
          {translateBackend(options.customTitle)}
        </Typography>
        <Button
          label={t('shared:viewAll')}
          variant='text'
          onClick={() => {
            navigate('/workflows');
          }}
        />
      </CardContent>
      <Table sx={{ minWidth: 650 }} aria-label='table'>
        <TableBody>
          <TableRow>
            <TableHeadCell>{t('table.client')}</TableHeadCell>
            <TableHeadCell>{t('table.workflow')}</TableHeadCell>
            <TableHeadCell>{t('table.section')}</TableHeadCell>
            <TableHeadCell>{t('table.step')}</TableHeadCell>
            <TableHeadCell>{t('table.started')}</TableHeadCell>
          </TableRow>
          {loading &&
            !previousData &&
            [...Array(15)].map((_x: any, i: number) => (
              <TableRow key={i}>
                <TableCell>
                  <Skeleton width='100%' />
                </TableCell>
                <TableCell>
                  <Skeleton width='100%' />
                </TableCell>
                <TableCell>
                  <Skeleton width='100%' />
                </TableCell>
                <TableCell>
                  <Skeleton width='100%' />
                </TableCell>
                <TableCell>
                  <Skeleton width='100%' />
                </TableCell>
              </TableRow>
            ))}
          {(
            data || previousData
          )?.fetchWorkflowCompletions?.workflowCompletions?.map(
            (workflowCompletion: any) => (
              <TableRow
                hover
                key={workflowCompletion.id}
                sx={{
                  '&:last-child td, &:last-child th': { border: 0 },
                  textDecoration: 'none',
                  cursor: 'pointer',
                }}
                onClick={() =>
                  navigate(
                    `/clients/${workflowCompletion.objectId}?workflowCompletion=${workflowCompletion.id}`,
                  )
                }
              >
                <TableCell component='th' scope='row'>
                  {workflowCompletion.object.type === 'INDIVIDUAL'
                    ? `${workflowCompletion.object.firstName} ${' '} ${workflowCompletion.object.lastName}`
                    : workflowCompletion.object.entityName}
                </TableCell>
                <TableCell>
                  {translateBackend(workflowCompletion.workflow.name)}
                </TableCell>
                <TableCell>
                  {translateBackend(workflowCompletion.currentStep.name)}
                </TableCell>
                <TableCell>
                  {workflowCompletion?.currentSubStep?.options?.title
                    ? translateBackend(
                        workflowCompletion?.currentSubStep?.options?.title,
                      )
                    : t(
                        `subStepTypes.${workflowCompletion?.currentSubStep?.type}`,
                      )}
                </TableCell>
                <TableCell>
                  {localizedDateTime(workflowCompletion.createdAt)}
                </TableCell>
              </TableRow>
            ),
          )}
        </TableBody>
      </Table>
    </Card>
  );
};
